import { ReactElement, ReactNode, useCallback, useMemo, useState } from "react";
import { CustomerDetails } from "../../models/customers/customerDetails";
import IntentToPayService from "../../models/intentToPay/intentToPayService";
import IntentToPayRequestProviderData from "../../models/paymentProcess/intentToPayRequestProviderData";
import { IntentToPayResult, IntentToPayTypeResult } from "../../models/paymentProcess/intentToPayResult";
import IntentToPayResultProviderData from "../../models/paymentProcess/intentToPayResultProviderData";
import { PaymentIntentItem } from "../../models/paymentProcess/payment-intent-item";
import { cancelPaymentIntent, registerPaymentIntent } from "../../services/paymentProcess/payment-process-service";
import { IntentToPayServiceContext } from "./IntentToPayContext";
import { PaymentMethodType } from "../../models/paymentProviders/paymentMethodType";

interface IntentToPayServiceProviderProps {
  children: ReactNode;
}

/*
 * Transaction cart
 */
const IntentToPayServiceProvider = (props: IntentToPayServiceProviderProps): ReactElement => {
  const { children } = props;
  const [intentToPayResult, setIntentToPayResult] = useState<IntentToPayResult>();

  const getProviderData = useCallback(
    <T extends IntentToPayRequestProviderData>() => intentToPayResult?.providerData as T,
    [intentToPayResult]
  );

  const handleCancelPaymentIntent = useCallback(async (): Promise<void> => {
    if (intentToPayResult) {
      try {
        const id = intentToPayResult.customerTransactionId;
        setIntentToPayResult(undefined);
        await cancelPaymentIntent(id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error cancelling payment intent:", error);
        throw error;
      }
    }
  }, [intentToPayResult]);

  const handleRegisterPaymentIntent = useCallback(
    async <T extends IntentToPayResultProviderData>(
      customerDetails: CustomerDetails,
      paymentProviderConfigurationId: string,
      paymentIntentItems: PaymentIntentItem[],
      paymentMethodType: PaymentMethodType,
      providerData?: IntentToPayRequestProviderData
    ): Promise<IntentToPayTypeResult<T>> => {
      // Cancel any current intent to pay we have
      if (intentToPayResult) {
        await handleCancelPaymentIntent();
      }

      try {
        // Register the new intent to pay
        const result = await registerPaymentIntent<IntentToPayRequestProviderData, T>(
          customerDetails,
          paymentProviderConfigurationId,
          paymentIntentItems,
          paymentMethodType,
          providerData
        );
        setIntentToPayResult(result);
        return result;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error registering payment intent:", error);
        throw error;
      }
    },
    [handleCancelPaymentIntent, intentToPayResult]
  );

  const context: IntentToPayService = useMemo(
    () => ({
      intentToPayResult,
      getProviderData,
      registerPaymentIntent: handleRegisterPaymentIntent,
      cancelPaymentIntent: handleCancelPaymentIntent,
      clearPaymentIntent: () => setIntentToPayResult(undefined),
    }),
    [getProviderData, intentToPayResult, handleCancelPaymentIntent, handleRegisterPaymentIntent]
  );

  return <IntentToPayServiceContext.Provider value={context}>{children}</IntentToPayServiceContext.Provider>;
};

export default IntentToPayServiceProvider;
